import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddLinkIcon from '@mui/icons-material/AddLink'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import { useNovoPortal } from '../estado'
import { marcaDoAmbiente } from '../tema'
import VinculoDialog, { ItemVinculo } from '../componentes/VinculoDialog'
import Janela from '../componentes/Janela'
import { caminhoDoDataset, conferirLink, LinkPowerBi, OPCOES_ATUALIZACAO, plural } from './comum'
import CampoDataset from './CampoDataset'
import ConfigIndicador from './ConfigIndicador'
import { useAuth } from '../../hooks/useAuth'
import './gestao.scss'

interface Vinculo { id: number; groupId: number; group?: { id: number; name: string; environmentId: number }; dashboardRuleId?: number | null; aiAgentId?: number | null }
interface Visao { environmentName: string; environmentId: number; groupName: string; groupId: number; filtersCount: number; teamsCount: number; usersCount: number }
interface Dashboard {
  id: number; name: string; reportPath: string; type?: string; active: boolean; order?: number | null
  workspaceId?: string | null; datasetId?: string | null; datasetWorkspaceId?: string | null
  kpiMeasure?: string | null; kpiDateColumn?: string | null; kpiFormat?: string | null; kpiHigherIsBetter?: boolean
  autoRefreshTime?: number | null; enableFilters: boolean; enableNavigationBar: boolean
  dashboardGroups?: Vinculo[]; dashboardFilters?: { environmentId?: number | null }[]
}
interface Form { name: string; reportPath: string; datasetPath: string; order: string; autoRefreshTime: string; enableFilters: boolean; enableNavigationBar: boolean; active: boolean }

const paraForm = (d: Dashboard): Form => ({
  name: d.name, reportPath: d.reportPath || '', datasetPath: caminhoDoDataset(d), order: d.order == null ? '' : String(d.order), autoRefreshTime: d.autoRefreshTime ? String(d.autoRefreshTime) : '',
  enableFilters: !!d.enableFilters, enableNavigationBar: !!d.enableNavigationBar, active: !!d.active
})

/**
 * O grupo do menu, escolhido entre os que já existem nos ambientes marcados. Criar grupo é em
 * Gestão › Ambientes; aqui só valem os que existem em todos os escolhidos.
 */
function CampoGrupo({ ambientes, valor, aoMudar }: { ambientes: number[]; valor: string; aoMudar: (v: string) => void }) {
  const [grupos, setGrupos] = useState<{ nome: string; ambientes: number }[] | null>(null)
  const chave = ambientes.slice().sort((a, b) => a - b).join(',')
  useEffect(() => {
    if (!chave) { setGrupos(null); return }
    let vale = true
    const t = setTimeout(() => services.api.novoPortalGruposDosAmbientes(chave.split(',')).then((r: any) => { if (vale) setGrupos(r.fail ? [] : r.data || []) }), 250)
    return () => { vale = false; clearTimeout(t) }
  }, [chave])
  const total = chave ? chave.split(',').length : 0
  const emTodos = (grupos || []).filter(g => g.ambientes === total)
  useEffect(() => { if (valor && grupos && !emTodos.some(g => g.nome === valor)) aoMudar('') }, [grupos]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <label className="nv-campo"><span>Grupo no menu</span>
      <select value={valor} onChange={e => aoMudar(e.target.value)} disabled={!total || !grupos}>
        <option value="">{!total ? 'Escolha os ambientes primeiro' : !grupos ? 'Carregando…' : emTodos.length ? 'Escolha o grupo' : 'Nenhum grupo em comum'}</option>
        {emTodos.map(g => <option key={g.nome} value={g.nome}>{g.nome}</option>)}
        {(grupos || []).filter(g => g.ambientes < total).map(g => <option key={g.nome} value={g.nome} disabled>{g.nome} (falta em {total - g.ambientes})</option>)}
      </select>
      <small>{total && grupos && !emTodos.length ? 'Os ambientes escolhidos não têm grupo em comum: vincule em partes ou crie o grupo em Gestão › Ambientes.' : 'Só aparecem os grupos que existem nos ambientes escolhidos.'}</small>
    </label>
  )
}

/** Gestão › Dashboards › um dashboard: o que vale em todos os ambientes e onde ele aparece. */
export default function DashboardGeral() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify } = useNotification()
  const est = useNovoPortal()
  const { getUserRoles } = useAuth()
  const admin = ((getUserRoles() || []) as string[]).includes('bi_dashboards_read_any')
  const [dash, setDash] = useState<Dashboard | null>(null)
  const [visoes, setVisoes] = useState<Visao[]>([])
  const [ambientes, setAmbientes] = useState<{ id: number; name: string }[]>([])
  const [regras, setRegras] = useState<Record<number, string>>({})
  const [form, setForm] = useState<Form | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [linkConferido, setLinkConferido] = useState<LinkPowerBi | string | null>(null)
  const [buscaAmb, setBuscaAmb] = useState('')
  const [vincular, setVincular] = useState(false)
  const [grupo, setGrupo] = useState('')
  const [copiarDe, setCopiarDe] = useState('')
  const [remover, setRemover] = useState<Visao | null>(null)
  const [datasetDoRelatorio, setDatasetDoRelatorio] = useState<{ caminho: string; nome?: string | null } | null>(null)
  const [datasetConferido, setDatasetConferido] = useState<string | null>(null)
  const [excluir, setExcluir] = useState(false)
  const [duplicar, setDuplicar] = useState<{ nome: string; acessos: boolean } | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const carregar = useCallback(async () => {
    const [d, v] = await Promise.all([services.api.getDashboardById(id), services.api.getDashboardEnvOverview(id)])
    if (d.fail || !d.data) { setErro(d.error || 'Dashboard não encontrado.'); return }
    setDash(d.data)
    setForm(paraForm(d.data))
    // O dataset que o relatório usa: confere o gravado, e preenche quando ainda não tem.
    if (d.data.type !== 'html' && d.data.reportPath) {
      conferirLink(d.data.reportPath).then(r => {
        if (typeof r === 'string' || !r.datasetCaminho) return
        const sugerido = { caminho: r.datasetCaminho, nome: r.datasetNome }
        setDatasetDoRelatorio(sugerido)
        if (!caminhoDoDataset(d.data)) setForm(f => (f ? { ...f, datasetPath: sugerido.caminho } : f))
      })
    }
    setVisoes(v.fail ? [] : (v.data || []).sort((a: Visao, b: Visao) => a.environmentName.localeCompare(b.environmentName)))
  }, [id])

  useEffect(() => {
    carregar()
    services.api.getEnvironmentsAll().then((r: any) => { if (!r.fail) setAmbientes((r.data || []).map((a: any) => ({ id: a.id, name: a.name }))) })
    services.api.getAllDashboardRules().then((r: any) => {
      if (!r.fail) setRegras(Object.fromEntries((r.data || []).map((x: any) => [x.id, x.name])))
    })
  }, [carregar])

  // Link mudou: confere no Power BI de novo.
  useEffect(() => {
    if (!dash || !form || form.reportPath === dash.reportPath) { setLinkConferido(null); return }
    const t = setTimeout(async () => {
      const r = await conferirLink(form.reportPath)
      setLinkConferido(r)
      // Relatório novo: o dataset acompanha, a não ser que a pessoa tenha colado outro.
      if (typeof r !== 'string' && r.datasetCaminho) {
        const sugerido = { caminho: r.datasetCaminho, nome: r.datasetNome }
        setForm(f => (f && (!f.datasetPath || f.datasetPath === datasetDoRelatorio?.caminho) ? { ...f, datasetPath: sugerido.caminho } : f))
        setDatasetDoRelatorio(sugerido)
      }
    }, 500)
    return () => clearTimeout(t)
  }, [form?.reportPath, dash]) // eslint-disable-line react-hooks/exhaustive-deps

  const vinculoDe = (envId: number) => dash?.dashboardGroups?.find(g => g.group?.environmentId === envId)

  if (erro) return <div className="ng"><p className="ng-explic">{erro}</p><Link to="/app/gestao/dashboards">Voltar ao catálogo</Link></div>
  if (!dash || !form) return <div className="ng"><p className="ng-explic">Carregando…</p></div>

  const html = dash.type === 'html'
  const linkMudou = form.reportPath !== dash.reportPath
  const linkOk = !linkMudou || (typeof linkConferido === 'object' && linkConferido !== null && linkConferido.tipo === (html ? 'html' : 'powerbi'))
  const mudou = JSON.stringify(form) !== JSON.stringify(paraForm(dash))
  const ordemOk = form.order === '' || /^\d{1,4}$/.test(form.order)
  const datasetMexido = form.datasetPath !== paraForm(dash).datasetPath || linkMudou
  const datasetOk = html || !!datasetConferido || !datasetMexido
  const valido = form.name.trim().length >= 3 && linkOk && ordemOk && datasetOk
  // Por que o salvar está desligado, em uma frase.
  const motivo = !mudou ? null : form.name.trim().length < 3 ? 'O nome precisa de ao menos 3 letras.' : !linkOk ? 'Confira o link do relatório.'
    : !ordemOk ? 'A ordem aceita só números.' : !datasetOk ? 'Confira o dataset: ele precisa ser encontrado no Power BI.' : null
  const semDatasetGravado = !html && !caminhoDoDataset(dash)

  const salvar = async () => {
    setSalvando(true)
    const caminho = linkMudou && typeof linkConferido === 'object' && linkConferido ? linkConferido.caminho : form.reportPath
    const res = await services.api.editDashboard({
      id: dash.id, name: form.name.trim(), reportPath: caminho, order: form.order === '' ? null : Number(form.order), active: form.active,
      datasetPath: html ? null : datasetConferido,
      autoRefreshTime: form.autoRefreshTime === '' ? null : Number(form.autoRefreshTime),
      enableFilters: form.enableFilters, enableNavigationBar: form.enableNavigationBar
    })
    setSalvando(false)
    if (res.fail) { notify(res.error || 'Não foi possível salvar.', { variant: 'error' }); return }
    notify('Configurações salvas. Valem em todos os ambientes onde o dashboard aparece.', { variant: 'success' })
    carregar()
  }

  const confirmarRemover = async () => {
    if (!remover) return
    setOcupado(true)
    const res = await services.api.deleteDashboardGroup(dash.id, remover.groupId)
    setOcupado(false)
    if (res.fail) { notify(res.error || 'Não foi possível remover do ambiente.', { variant: 'error' }); return }
    notify(`${dash.name} foi removido de ${remover.environmentName}.`, { variant: 'success' })
    setRemover(null)
    carregar()
  }

  const confirmarExcluir = async () => {
    setOcupado(true)
    const res = await services.api.deleteDashboard(dash.id)
    setOcupado(false)
    if (res.fail) { notify(res.error || 'Não foi possível excluir.', { variant: 'error' }); return }
    notify(`${dash.name} foi excluído.`, { variant: 'success' })
    navigate('/app/gestao/dashboards')
  }

  const confirmarDuplicar = async () => {
    if (!duplicar) return
    setOcupado(true)
    const res = await services.api.novoPortalDuplicarDashboard(dash.id, { nome: duplicar.nome, copiarAcessos: duplicar.acessos })
    setOcupado(false)
    if (res.fail) { notify(res.error || 'Não foi possível duplicar.', { variant: 'error' }); return }
    const r = res.data
    notify(`Cópia criada: ${plural(r.versoes, 'versão', 'versões')}, ${plural(r.disparos, 'disparo', 'disparos')} (pausados) e ${plural(r.ambientes, 'ambiente', 'ambientes')}.`, { variant: 'success' })
    setDuplicar(null)
    navigate(`/app/gestao/dashboards/${r.id}`)
  }

  const itensAmbientes: ItemVinculo[] = ambientes.map(a => {
    const marca = marcaDoAmbiente(est.marcas, est.ambientes, a.id).nome
    return { id: String(a.id), nome: a.name, etiqueta: marca, busca: marca, tipo: marca, ja: visoes.some(v => v.environmentId === a.id) ? 'Já aparece' : undefined }
  }).sort((x, y) => x.nome.localeCompare(y.nome))
  const marcasDisponiveis = Array.from(new Set(itensAmbientes.map(i => i.tipo!))).sort()

  const abrirVincular = () => {
    setGrupo('')
    setCopiarDe(visoes[0] ? String(visoes[0].environmentId) : '')
    setVincular(true)
  }

  const q = buscaAmb.trim().toLowerCase()
  const linhas = visoes.filter(v => !q || v.environmentName.toLowerCase().includes(q) || v.groupName.toLowerCase().includes(q))
  const origem = visoes.find(v => String(v.environmentId) === copiarDe)

  return (
    <div className="ng">
      <nav className="ng-trilha" aria-label="Caminho"><Link to="/app/gestao/dashboards">Dashboards</Link><span>›</span><span>{dash.name}</span></nav>
      <div className="ng-cab">
        <h1>{dash.name} <span className="ng-pill">{html ? 'Relatório HTML' : 'Power BI'}</span>{!dash.active && <span className="ng-pill inativo">Inativo</span>}</h1>
        <div className="ng-acoes">
          {html && <Link className="ng-btn" to={`/app/gestao/dashboards/${dash.id}/html`}>Editar relatório HTML</Link>}
          {visoes[0] && <Link className="ng-btn" to={`/app/dashboard/${dash.id}/${visoes[0].environmentId}`}>Abrir dashboard</Link>}
          <button type="button" className="ng-btn" onClick={() => setDuplicar({ nome: `${dash.name} (cópia)`.slice(0, 100), acessos: false })}>Duplicar</button>
          <button type="button" className="ng-btn perigo" onClick={() => setExcluir(true)}>Excluir</button>
        </div>
      </div>

      <section className="ng-cartao">
        <h2>Configurações gerais</h2>
        <p className="ng-explic">Valem em todos os ambientes onde o dashboard aparece.</p>
        <div className="ng-campos">
          <label className="ng-campo"><span>Nome</span>
            <input value={form.name} maxLength={100} onChange={e => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="ng-campo"><span>Ordem no menu</span>
            <input value={form.order} inputMode="numeric" placeholder="Sem ordem: por nome" onChange={e => setForm({ ...form, order: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
            <small>Menor aparece primeiro dentro do grupo. Vale em todos os ambientes.</small>
          </label>
          {!html ? (
            <label className="ng-campo"><span>Atualizar a tela sozinho</span>
              <select value={form.autoRefreshTime} onChange={e => setForm({ ...form, autoRefreshTime: e.target.value })}>
                {OPCOES_ATUALIZACAO.map(o => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                {form.autoRefreshTime && !OPCOES_ATUALIZACAO.some(o => o.valor === form.autoRefreshTime) && <option value={form.autoRefreshTime}>A cada {form.autoRefreshTime} s</option>}
              </select>
            </label>
          ) : <span />}
          <label className="ng-campo largo"><span>{html ? 'Link do dataset' : 'Link do relatório'} no Power BI</span>
            <input value={form.reportPath} onChange={e => setForm({ ...form, reportPath: e.target.value })} />
            {!linkMudou && <small>Para trocar, cole o link novo copiado do Power BI.</small>}
            {linkMudou && linkConferido === null && <small>Conferindo no Power BI…</small>}
            {linkMudou && typeof linkConferido === 'string' && <small className="erro">{linkConferido}</small>}
            {linkMudou && typeof linkConferido === 'object' && linkConferido && (linkConferido.tipo === (html ? 'html' : 'powerbi')
              ? <small className="ok">Encontrado no Power BI: {linkConferido.nome}</small>
              : <small className="erro">Esse link é de {linkConferido.tipo === 'html' ? 'um dataset' : 'um relatório'}; este dashboard é {html ? 'relatório HTML e usa dataset' : 'Power BI e usa relatório'}.</small>)}
          </label>
          {!html && (
            <div className="largo">
              <CampoDataset classe="ng-campo" valor={form.datasetPath} aoMudar={v => setForm({ ...form, datasetPath: v })} aoConferir={setDatasetConferido} doRelatorio={datasetDoRelatorio} />
              {semDatasetGravado && form.datasetPath && <small style={{ display: 'block', marginTop: 6, color: '#b26a00', fontWeight: 700 }}>Este dashboard ainda não tinha dataset gravado. Preenchemos com o do relatório: salve para gravar.</small>}
            </div>
          )}
        </div>
        <div className="ng-toggles">
          {!html && <label className="ng-toggle"><input type="checkbox" checked={form.enableFilters} onChange={e => setForm({ ...form, enableFilters: e.target.checked })} />Mostrar o painel de filtros do Power BI</label>}
          {!html && <label className="ng-toggle"><input type="checkbox" checked={form.enableNavigationBar} onChange={e => setForm({ ...form, enableNavigationBar: e.target.checked })} />Mostrar as páginas do relatório (navegação)</label>}
          <label className="ng-toggle"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />Ativo (inativo some do menu de todos os ambientes)</label>
        </div>
        <div className="ng-rodape-cartao">
          <button type="button" className="ng-btn prim" disabled={!mudou || !valido || salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar alterações'}</button>
          {mudou && <button type="button" className="ng-btn" onClick={() => setForm(paraForm(dash))}>Desfazer</button>}
          {motivo && <small style={{ color: '#c4362b', fontWeight: 700 }}>{motivo}</small>}
        </div>
      </section>

      {admin && <ConfigIndicador key={`${dash.id}-${dash.datasetId}-${dash.kpiMeasure}`} dash={dash} aoSalvar={carregar} />}

      <section className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Ambientes onde aparece ({visoes.length})</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={abrirVincular}><AddLinkIcon />Vincular a ambientes</button></div>
        </div>
        <p className="ng-explic">Em cada ambiente o dashboard tem grupo, regra, filtros, acessos e oneflow próprios.</p>
        {visoes.length > 8 && (
          <div className="ng-filtros" style={{ marginBottom: 8 }}>
            <label className="ng-busca"><SearchIcon fontSize="small" />
              <input value={buscaAmb} onChange={e => setBuscaAmb(e.target.value)} placeholder="Buscar ambiente ou grupo" aria-label="Buscar ambiente ou grupo" />
            </label>
          </div>
        )}
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Ambiente</th><th>Grupo</th><th>Regra</th><th className="n">Filtros</th><th>Acesso</th><th /></tr></thead>
            <tbody>
              {visoes.length === 0 && <tr><td colSpan={6} className="vazio">Ainda em nenhum ambiente. Use “Vincular a ambientes”.</td></tr>}
              {linhas.map(v => {
                const vinc = vinculoDe(v.environmentId)
                const regra = vinc?.dashboardRuleId ? regras[vinc.dashboardRuleId] : null
                return (
                  <tr key={v.environmentId}>
                    <td><span className="ng-amb"><span className="ng-amb-ini">{v.environmentName.charAt(0)}</span><span>{v.environmentName} <span className="ng-tag">{marcaDoAmbiente(est.marcas, est.ambientes, v.environmentId).nome}</span></span></span></td>
                    <td>{v.groupName}</td>
                    <td>{regra || <span style={{ color: '#8b92a1' }}>Sem regra</span>}</td>
                    <td className="n">{v.filtersCount}</td>
                    <td>{v.teamsCount || v.usersCount ? [v.teamsCount ? plural(v.teamsCount, 'equipe', 'equipes') : '', v.usersCount ? plural(v.usersCount, 'pessoa', 'pessoas') : ''].filter(Boolean).join(' · ') : <span style={{ color: '#c4362b', fontWeight: 700 }}>Ninguém</span>}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <Link className="ng-btn" to={`/app/gestao/dashboards/${dash.id}/ambiente/${v.environmentId}`}>Configurar</Link>{' '}
                      <button type="button" className="ng-btn perigo" onClick={() => setRemover(v)}>Remover</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <VinculoDialog
        aberto={vincular}
        titulo="Vincular a ambientes"
        sub={`${dash.name} · ${plural(visoes.length, 'ambiente', 'ambientes')} hoje`}
        carregando={ambientes.length === 0}
        depois={sel => (
          <div className="nv-campos">
            <CampoGrupo ambientes={(sel.amb || []).map(i => Number(i.id))} valor={grupo} aoMudar={setGrupo} />
            {visoes.length > 0 && (
              <label className="nv-campo"><span>Começar com</span>
                <select value={copiarDe} onChange={e => setCopiarDe(e.target.value)}>
                  {visoes.map(v => <option key={v.environmentId} value={v.environmentId}>Regra, filtros e acessos de {v.environmentName}</option>)}
                  <option value="">Configuração em branco</option>
                </select>
              </label>
            )}
          </div>
        )}
        abas={[{
          id: 'amb', rotulo: 'Ambientes', dica: 'Buscar ambiente ou marca', unidade: ['ambiente', 'ambientes'], itens: itensAmbientes,
          filtros: marcasDisponiveis.length > 1 ? marcasDisponiveis.map(m => ({ id: m, rotulo: m, teste: (i: ItemVinculo) => i.tipo === m })) : undefined
        }]}
        resumo={(sel, total) => total
          ? <><b>{dash.name}</b> passa a aparecer em <b>{plural(total, 'ambiente', 'ambientes')}</b>, no grupo <b>{grupo.trim() || '…'}</b>, {origem ? <>com a regra, os filtros e os acessos de <b>{origem.environmentName}</b>.</> : <span className="alerta">sem ninguém com acesso ainda.</span>}</>
          : <>Depois de vincular, confira em cada ambiente quem acessa, a regra e os filtros.</>}
        botao={total => total ? `Vincular a ${plural(total, 'ambiente', 'ambientes')}` : 'Vincular'}
        aoFechar={() => setVincular(false)}
        aoConfirmar={async sel => {
          if (!grupo.trim()) return 'Escolha o grupo do menu.'
          const r = await services.api.novoPortalVincularDashboard(dash.id, {
            ambientes: (sel.amb || []).map(i => Number(i.id)), grupo: grupo.trim(), copiarDe: copiarDe ? Number(copiarDe) : null
          })
          if (r.fail) return r.error || 'Não foi possível vincular.'
          setVincular(false)
          const partes = [`Vinculado a ${plural(r.data.vinculados, 'ambiente', 'ambientes')}`]
          if (r.data.gruposCriados) partes.push(`${plural(r.data.gruposCriados, 'grupo criado', 'grupos criados')}`)
          if (r.data.jaEstavam?.length) partes.push(`já estava em ${r.data.jaEstavam.join(', ')}`)
          notify(partes.join('; ') + '.', { variant: 'success' })
          carregar()
          return null
        }}
      />

      <Janela aberto={!!remover} titulo="Remover deste ambiente" botao="Remover do ambiente" perigo salvando={ocupado}
              aoConfirmar={confirmarRemover} aoFechar={() => setRemover(null)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Remover <b>{dash.name}</b> de <b>{remover?.environmentName}</b>? Ele some do menu de todos ali, e os acessos, a regra e os filtros deste ambiente se perdem. Nos outros ambientes nada muda.</p>
      </Janela>

      <Janela aberto={!!duplicar} titulo="Duplicar dashboard" botao="Duplicar" salvando={ocupado} desabilitado={!duplicar?.nome.trim()}
              aoConfirmar={confirmarDuplicar} aoFechar={() => setDuplicar(null)}>
        <label className="nv-campo"><span>Nome da cópia</span>
          <input value={duplicar?.nome ?? ''} maxLength={100} onChange={e => setDuplicar(d => (d ? { ...d, nome: e.target.value } : d))} />
        </label>
        <p style={{ margin: '8px 0', lineHeight: 1.5 }}>
          Vai junto: dicionário, seletor de loja, template, indicador, filtros padrão, os ambientes (com regra e agente), todas as versões do HTML e os
          <b> disparos de e-mail, que ficam pausados</b> para a cópia não mandar e-mail repetido. Ative cada um quando quiser.
        </p>
        <label className="nv-campo" style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={!!duplicar?.acessos} style={{ marginTop: 3 }}
                 onChange={e => setDuplicar(d => (d ? { ...d, acessos: e.target.checked } : d))} />
          <span>Copiar também quem tem acesso (pessoas e equipes) e os valores da regra por pessoa. Sem isso, só a gestão enxerga a cópia.</span>
        </label>
      </Janela>

      <Janela aberto={excluir} titulo="Excluir dashboard" botao="Excluir de vez" perigo salvando={ocupado}
              aoConfirmar={confirmarExcluir} aoFechar={() => setExcluir(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Excluir <b>{dash.name}</b>? Ele sai dos {plural(visoes.length, 'ambiente', 'ambientes')} onde aparece, com acessos, filtros e favoritos. Para só esconder, desmarque “Ativo” nas configurações gerais.</p>
      </Janela>
    </div>
  )
}
